import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { useCategory } from '../context/CategoryContext';
import './CategoryProducts.css';

const CategoryProducts = () => {
  const { category } = useParams();
  const { categories, fetchProductsByCategory, loading, error, productsByCategory } = useCategory();
  const [isFetching, setIsFetching] = useState(false);

  const categoryIdFromRoute = /^\d+$/.test(String(category)) ? Number(category) : null;

  // Find the current category from the categories list
  const currentCategory = categories.find((cat) => {
    if (categoryIdFromRoute != null) {
      return Number(cat?.id) === categoryIdFromRoute;
    }
    return cat.name?.toLowerCase().replace(/\s+/g, '-') === category;
  });

  // Fetch products for this category
  useEffect(() => {
    const loadProducts = async () => {
      if (currentCategory?.id) {
        // Check if products are already cached
        if (productsByCategory[currentCategory.id]) {
          return; // Already loaded
        }

        setIsFetching(true);
        try {
          await fetchProductsByCategory(currentCategory.id);
        } catch (err) {
          console.error('Error loading products:', err);
        } finally {
          setIsFetching(false);
        }
      }
    };

    loadProducts();
  }, [currentCategory?.id, fetchProductsByCategory, productsByCategory]);

  // Format price with KSh
  const formatPrice = (price) => {
    return `KSh ${price}`;
  };

  // Build SEO data
  const categoryName =
    currentCategory?.name ||
    (categoryIdFromRoute != null ? 'Category Products' : category?.replace(/-/g, ' '));
  const categoryTitle = categoryName ? `${categoryName} - Interior Products in Nairobi, Kenya | Techie Interiors` : 'Shop by Category | Techie Interiors';
  const categoryDescription = categoryName 
    ? `Browse our collection of ${categoryName.toLowerCase()} in Nairobi Kenya. High-quality ${categoryName.toLowerCase()} with expert installation services across Kenya.`
    : 'Browse interior design products and furniture by category. Shop gypsum ceilings, wardrobes, kitchen cabinets, and more from Techie Interiors Kenya.';
  
  const structuredData = currentCategory ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${categoryName} - Techie Interiors`,
    "description": categoryDescription,
    "url": `https://techieinteriors.co.ke/category/${category}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Techie Interiors",
      "url": "https://techieinteriors.co.ke"
    }
  } : null;

  // Handle wishlist toggle
  const handleWishlistToggle = async (product) => {
    console.log('Wishlist toggled for:', product.name);
  };

  // Format category name for display
  const formatCategoryName = (name) => {
    if (!name) return 'Products';
    if (/^\d+$/.test(String(name))) {
      return currentCategory?.name || 'Products';
    }
    return name.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const displayCategoryName = currentCategory?.name || formatCategoryName(category);

  return (
    <div className="category-products-container">
      {/* SEO Component — noindex because content is fully API-driven and may be thin/duplicate */}
      <SEO
        title={categoryTitle}
        description={categoryDescription}
        image="/techLogo.jpeg"
        url={`https://techieinteriors.co.ke/category/${category}`}
        structuredData={structuredData}
        noindex={true}
      />
      <div className="category-products-header">
        <h1 className="category-title">{displayCategoryName}</h1>
        <p className="category-description">
          Explore our curated collection of {displayCategoryName.toLowerCase()} products
        </p>
      </div>
      
      {(loading || isFetching) && <div className="loading-message">Loading products...</div>}
      {error && <div className="error-message">Error: {error}</div>}
      
      {!loading && !isFetching && !error && (
        <div className="category-products-grid">
          {productsByCategory[currentCategory?.id]?.length > 0 ? (
            productsByCategory[currentCategory?.id].map((product) => {
              // Calculate discounted price
              const numericPrice = typeof product.price === 'string' 
                ? parseFloat(product.price.replace(/[KSh$\s]/g, '')) || 0
                : product.price || 0;
              
              const discountedPrice = product.discount > 0 
                ? numericPrice * (1 - product.discount / 100)
                : numericPrice;
              
              // Format product for ProductCard component
              const formattedProduct = {
                id: product.id,
                name: product.name,
                description: product.description?.replace(/<[^>]*>/g, '').substring(0, 100) + '...' || '',
                price: formatPrice(discountedPrice.toFixed(2)),
                originalPrice: product.discount > 0 ? formatPrice(numericPrice.toFixed(2)) : null,
                discount: product.discount || 0,
                image_fullpath: product.image_fullpath || product.image || [],
                image: product.image_fullpath?.[0] || product.image?.[0] || 'https://via.placeholder.com/400x300',
                category: currentCategory?.name || 'Uncategorized'
              };
              
              return (
                <ProductCard 
                  key={product.id} 
                  product={formattedProduct} 
                  onWishlistToggle={handleWishlistToggle}
                />
              );
            })
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <h3 className="empty-state-title">No Products Available</h3>
              <p className="empty-state-description">
                We're currently updating our {formatCategoryName(category).toLowerCase()} collection.
                Please check back soon or explore our other categories!
              </p>
              <Link to="/shop" className="empty-state-button">Browse All Products</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryProducts;